import * as React from 'react';
import * as config from './config';
import * as globalStyles from 'styles/style.scss';
import * as styles from './styles.scss';
import {
  AccountDropdown,
  DateRangePicker,
  Fallback,
  SheetData,
  SymbolSearch,
} from 'components/common';
import { BrokerFunction, IAccount, IBrokerPhoneCode } from 'interfaces/common';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { IEquityRightSubscriptionsParams } from 'interfaces/api';
import { IState } from 'redux/global-reducers';
import { QUERY_FETCH_COUNT } from 'constants/main';
import { SystemType } from 'constants/enum';
import { WithNamespaces, withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { formatDateToString } from 'utils/datetime';
import { isString } from 'lodash';
import { withErrorBoundary } from 'react-error-boundary';
import { showNotification } from 'redux/global-actions';
import { Button } from 'react-bootstrap';
import { ToastType } from 'react-toastify';
import {
  getAuthorizedBroker,
  handleError,
  getDayDifference,
} from 'utils/common';
import { queryRightExerciseSubscriptions } from './action';
import BrokerPhoneCode from 'components/common/BrokerPhoneCode';

interface IRightSubscriptionsProps
  extends React.ClassAttributes<RightSubscriptionsComponent>,
    WithNamespaces {
  readonly selectedAccount: IState['selectedAccount'];
  readonly rightExerciseSubscriptions: IState['equytyRightExerciseSubscriptions'];
  readonly limitDateQuery: IState['limitDateQuery'];
  readonly brokerVerifyPin: IState['brokerVerifyPin'];

  readonly queryRightExerciseSubscriptions: typeof queryRightExerciseSubscriptions;
  readonly showNotification: typeof showNotification;
}

interface IRightSubscriptionsState {
  readonly fromDate: Date;
  readonly toDate: Date;
  readonly accountNumber?: string;
  readonly symbolCode: string;
}

class RightSubscriptionsComponent extends React.Component<
  IRightSubscriptionsProps,
  IRightSubscriptionsState
> {
  private localGridApi?: GridApi;
  private localBrokerVerifyPin: IBrokerPhoneCode | null = getAuthorizedBroker(
    this.props.brokerVerifyPin,
    this.props.selectedAccount,
    BrokerFunction.RIGHT_SUBSCRIPTION
  );

  constructor(props: IRightSubscriptionsProps) {
    super(props);

    this.state = {
      fromDate: new Date(),
      toDate: new Date(),
      accountNumber: this.props.selectedAccount?.account,
      symbolCode: 'ALL',
    };
  }

  componentDidMount() {
    if (!this.props.selectedAccount?.isBrokerCall) {
      this.queryEquityRightSubscriptions();
    } else if (this.localBrokerVerifyPin?.isAuthorized) {
      this.queryEquityRightSubscriptions();
    }
    this.props.i18n.on('languageChanged', this.onChangeLang);
  }

  shouldComponentUpdate(nextProps: IRightSubscriptionsProps) {
    if (
      this.props.brokerVerifyPin !== nextProps.brokerVerifyPin ||
      this.props.selectedAccount !== nextProps.selectedAccount
    ) {
      if (nextProps.selectedAccount != null) {
        this.localBrokerVerifyPin = getAuthorizedBroker(
          nextProps.brokerVerifyPin,
          nextProps.selectedAccount,
          BrokerFunction.RIGHT_SUBSCRIPTION
        );

        if (
          this.localBrokerVerifyPin != null &&
          this.localBrokerVerifyPin.isAuthorized
        ) {
          this.queryEquityRightSubscriptions();
        }
      }
      this.forceUpdate();
    }
    if (this.props.selectedAccount !== nextProps.selectedAccount) {
      if (!nextProps.selectedAccount?.isBrokerCall) {
        this.queryEquityRightSubscriptions();
      }
    }
    return true;
  }

  componentWillUnmount() {
    this.props.i18n.off('languageChanged', this.onChangeLang);
  }

  render() {
    const { t, rightExerciseSubscriptions, selectedAccount } = this.props;

    const isShowPhoneCode =
      this.localBrokerVerifyPin &&
      this.localBrokerVerifyPin.clientID === selectedAccount?.clientID &&
      this.localBrokerVerifyPin.isAuthorized;

    return (
      <div className={styles.RightSubscriptions}>
        <div className={styles.RightSubsFilter}>
          <div className={styles.RightSubsAccount}>
            <p>{t('Account No.')}</p>
            <AccountDropdown
              isForm={true}
              onChange={this.onChangeAccount}
              type={SystemType.EQUITY}
            />
          </div>
          <div className={styles.RightSubsStockSymbol}>
            <p>{t('Stock Symbol')}</p>
            <div className={styles.RightSubsSymbolPicker}>
              <SymbolSearch
                placeholder={this.state.symbolCode}
                onSymbolSearch={this.onSymbolSearch}
                isClearable={true}
                isForm={true}
              />
            </div>
          </div>
          <div className={styles.RightSubsDate}>
            <p>{t('Date')}</p>
            <DateRangePicker
              onChangeStartDate={this.onChangeFromDate}
              onChangeEndDate={this.onChangeToDate}
              startDate={this.state.fromDate}
              endDate={this.state.toDate}
            />
          </div>
          {!isShowPhoneCode && this.props.selectedAccount?.isBrokerCall && (
            <BrokerPhoneCode
              brokerFunction={BrokerFunction.RIGHT_SUBSCRIPTION}
            />
          )}
          <div className={styles.RightSubsQuery}>
            <Button
              className={globalStyles.QueryButton}
              onClick={this.onClickQuery}
              disabled={rightExerciseSubscriptions.status.isLoading}
            >
              {t('Query')}
            </Button>
          </div>
        </div>
        <SheetData
          rowData={
            this.props.selectedAccount?.isBrokerCall
              ? this.localBrokerVerifyPin?.isAuthorized
                ? rightExerciseSubscriptions.data
                : []
              : rightExerciseSubscriptions.data
          }
          columnDefs={config.getColumnDefs()}
          status={rightExerciseSubscriptions.status}
          onGridReady={this.onGridReady}
          defaultColDef={config.DEFAULT_COL_DEF}
          onLoadMore={this.queryEquityRightSubscriptions}
        />
      </div>
    );
  }

  private onGridReady = (event: GridReadyEvent) => {
    this.localGridApi = event.api;
  };

  private onChangeLang = () => {
    setTimeout(() => {
      this.localGridApi?.sizeColumnsToFit();
    });
  };

  private onChangeFromDate = (date: Date) => {
    this.setState({ fromDate: date });
  };

  private onChangeToDate = (date: Date) => {
    this.setState({ toDate: date });
  };

  private onSymbolSearch = (code: string) => {
    this.setState({ symbolCode: code });
  };

  private onClickQuery = () => {
    if (this.props.selectedAccount?.isBrokerCall) {
      if (this.localBrokerVerifyPin?.isAuthorized) {
        this.queryEquityRightSubscriptions();
      } else {
        return;
      }
    } else if (
      !getDayDifference(
        this.state.fromDate,
        this.state.toDate,
        this.props.limitDateQuery
      )
    ) {
      this.props.showNotification({
        type: ToastType.ERROR,
        title: `Error`,
        content: `Maximum query limit is {{variable}} days`,
        contentParams: {
          variable: this.props.limitDateQuery.find((ele) => ele.limit_query)
            ?.limit_query,
        },
        time: new Date(),
      });
    } else {
      this.queryEquityRightSubscriptions();
    }
  };

  private onChangeAccount = (account: IAccount | string) => {
    if (isString(account)) {
      this.setState({ accountNumber: account });
    } else {
      this.setState({ accountNumber: account.account }, () => {
        this.queryEquityRightSubscriptions();
      });
    }
  };

  private queryEquityRightSubscriptions = (
    offset?: number,
    fetchCount?: number
  ) => {
    if (this.state.accountNumber != null) {
      const params: IEquityRightSubscriptionsParams = {
        accountNo: this.state.accountNumber,
        symbol: this.state.symbolCode || 'ALL',
        fromDate: formatDateToString(this.state.fromDate, 'yyyyMMdd'),
        toDate: formatDateToString(this.state.toDate, 'yyyyMMdd'),
        offset: offset || 0,
        fetchCount: fetchCount || QUERY_FETCH_COUNT,
      };
      if (this.props.selectedAccount?.isBrokerCall) {
        this.props.queryRightExerciseSubscriptions({
          ...params,
          clientID: this.props.selectedAccount.clientID,
          pin: this.localBrokerVerifyPin?.pin,
        });
      } else {
        this.props.queryRightExerciseSubscriptions(params);
      }
    }
  };
}

const mapStateToProps = (state: IState) => ({
  selectedAccount: state.selectedAccount,
  brokerVerifyPin: state.brokerVerifyPin,
  rightExerciseSubscriptions: state.equytyRightExerciseSubscriptions,
  limitDateQuery: state.limitDateQuery,
});

const mapDispatchToProps = {
  queryRightExerciseSubscriptions,
  showNotification,
};

const RightSubscriptions = withErrorBoundary(
  withNamespaces('common')(
    connect(mapStateToProps, mapDispatchToProps)(RightSubscriptionsComponent)
  ),
  Fallback,
  handleError
);

export default RightSubscriptions;



// WEBPACK FOOTER //
// ./src/components/areas/Account/RightSubscriptions/index.tsx